import { supabase } from "@/integrations/supabase/client";

/**
 * Shared transport for the agent chat hooks (TES-1128).
 *
 * Both `useAgentChat` (agent-orchestrator) and `useHelpChat` (help-chat) post a
 * user-JWT-scoped JSON request to an Edge Function that runs the same
 * `agent-orchestrator` handler and returns a single JSON response (NOT a
 * stream). This centralises the fetch boilerplate (auth header, apikey, JSON
 * parse) so the two hooks don't duplicate it.
 */

export interface AgentRequestBody {
  message: string;
  sessionId?: string;
  language: string;
  images?: Array<{ base64: string; mimeType: string }>;
}

export interface AgentRequestResult {
  ok: boolean;
  status: number;
  payload: {
    sessionId?: string;
    response?: string;
    toolResults?: Array<{ tool: string; success: boolean; result: Record<string, unknown> }>;
    error?: string;
  };
}

/**
 * POST a turn to an agent Edge Function and return the parsed JSON + status.
 * Throws only for genuine network/abort errors (the caller maps HTTP errors).
 *
 * @param fn      Edge Function slug (e.g. "agent-orchestrator" | "help-chat").
 * @param body    Request body.
 * @param signal  AbortSignal for cancellation.
 */
export async function postAgentTurn(
  fn: string,
  body: AgentRequestBody,
  signal: AbortSignal,
): Promise<AgentRequestResult> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  // Guard against a missing session — otherwise we'd send
  // `Authorization: Bearer undefined` and the user would see a generic 401
  // instead of a clear "session expired" signal. Surface it as a 401 result.
  if (!session?.access_token) {
    return { ok: false, status: 401, payload: { error: "not_authenticated" } };
  }
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${fn}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify(body),
    signal,
  });

  const payload = (await response.json().catch(() => ({}))) as AgentRequestResult["payload"];
  return { ok: response.ok, status: response.status, payload };
}

/** Map an HTTP status from an agent turn to a stable error code. */
export function agentErrorCode(status: number): string {
  if (status === 401) return "not_authenticated";
  if (status === 503) return "no_provider";
  if (status === 429) return "rate_limited";
  if (status === 422) return "image_not_supported";
  return "server_error";
}
